import { useCallback, useEffect, useState } from 'react';

/** How long each event holds the strip before the next one takes it. Long
 *  enough to read a title, a date and a sub-line without racing. */
const ROTATE_INTERVAL_MS = 8000;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Auto-advancing content is exactly what this setting is about, so a reader who
 *  has asked for less motion gets the strip parked on the first event with the
 *  controls still there to step through manually. Guarded for any environment
 *  without matchMedia (tests, SSR), where it reports "no preference". */
export function prefersReducedMotion(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

export interface EventRotation {
    readonly activeIndex: number;
    /** Jump to a slot and stop auto-advancing. Stopping is the point, not a
     *  side effect: with no pause control on the strip, picking a dot is the
     *  only way a reader can make it hold still, which WCAG 2.2.2 (Pause, Stop,
     *  Hide) asks for on content that auto-advances for longer than 5s. It does
     *  not resume - a reader who stopped it wanted it stopped. */
    readonly select: (index: number) => void;
    /** Pauses without ending auto-advance: pointer over the strip, focus inside
     *  it, or the detail modal open. Unlike `select` this reverses itself. */
    readonly setHeld: (held: boolean) => void;
}

/**
 * Drives the banner strip's rotation across `count` live events.
 *
 * Holds no event data on purpose: the strip owns the list, this owns only which
 * slot is showing. That keeps the clamping below honest - `useActiveEvents`
 * refetches on an interval, so the list can shrink under a pointer that is
 * nowhere near the strip, and an index left pointing past the end would render
 * `undefined`.
 */
export function useEventRotation(count: number): EventRotation {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(() => !prefersReducedMotion());
    const [isHeld, setHeld] = useState(false);

    useEffect(() => {
        setActiveIndex((index) => (count === 0 ? 0 : Math.min(index, count - 1)));
    }, [count]);

    const running = isPlaying && !isHeld && count > 1;

    useEffect(() => {
        if (!running) return undefined;
        const id = setInterval(() => {
            setActiveIndex((index) => (index + 1) % count);
        }, ROTATE_INTERVAL_MS);
        return () => clearInterval(id);
    }, [running, count]);

    const select = useCallback((index: number) => {
        setActiveIndex(index);
        setIsPlaying(false);
    }, []);

    return { activeIndex, select, setHeld };
}
